import { useEffect, useRef, useState } from 'react'
import type { Payable } from '@core/payments'
import { attachProofImage, attachProofNote, loadProof, type Proof } from '@core/proof'
import { Button, ErrorNote } from '@design/components'
import { DocumentThumb } from '@features/documents/DocumentThumb'

/**
 * Attaching proof that this was paid.
 *
 * spec.md §13: "offered, never required", and it "remains on the item
 * forever". So this is a quiet chip rather than a step — no dialog, nothing
 * blocking, and it does not ask again once something is attached.
 *
 * Worth having because the day a supplier insists they were never paid, a
 * screenshot from the bank ends the conversation in ten seconds. Worth
 * nothing on every other day, which is exactly why it must cost nothing.
 */
export function ProofChip({
  payable,
  userId,
  onView,
}: {
  payable: Payable
  userId: string
  onView?: (documentId: string) => void
}) {
  const [proofs, setProofs] = useState<Proof[] | null>(null)
  const [adding, setAdding] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const refresh = () => loadProof(payable).then(setProofs).catch(() => setProofs([]))

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payable.instalmentId])

  if (proofs === null) return null

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError('')
    try {
      await fn()
      await refresh()
      setAdding(false)
      setText('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Dat lukte niet.')
    }
    setBusy(false)
  }

  return (
    <div className="mt-4">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void run(() => attachProofImage(payable, file, userId))
        }}
      />

      {proofs.length > 0 && (
        <div className="mb-2">
          <p className="text-sm text-muted mb-1.5">Bewijs</p>
          <div className="flex flex-wrap gap-2">
            {proofs.map((p, i) =>
              p.documentId ? (
                <div key={i} className="w-16">
                  <DocumentThumb
                    documentId={p.documentId}
                    onOpen={() => onView?.(p.documentId!)}
                  />
                </div>
              ) : (
                <span key={i} className="text-sm px-2 py-1 rounded-lg bg-surface border border-line">
                  {p.text}
                </span>
              ),
            )}
          </div>
        </div>
      )}

      <ErrorNote>{error}</ErrorNote>

      {adding ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void run(() => attachProofNote(payable, text, userId, 'reference'))
          }}
        >
          <input
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Referentie van de bank, of een notitie"
            className="w-full min-h-[3rem] px-4 rounded-xl bg-surface border border-line mb-2"
          />
          <div className="flex gap-2">
            <Button type="submit" tone="secondary" disabled={busy || !text.trim()}>
              Bewaren
            </Button>
            <Button tone="quiet" type="button" onClick={() => setAdding(false)}>
              Terug
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="text-sm px-3 py-2 rounded-lg border border-line text-muted"
          >
            {proofs.length > 0 ? '+ foto' : 'Bewijs toevoegen'}
          </button>
          <button
            onClick={() => setAdding(true)}
            disabled={busy}
            className="text-sm px-3 py-2 rounded-lg border border-line text-muted"
          >
            + notitie
          </button>
        </div>
      )}
    </div>
  )
}
